import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AspectRatio } from '../AspectRatio';
import { Box } from '../Box';
import { styled } from '../../system';
import { IMAGE_SIZES_PROP, getInt, getSrcSet, isStaticImport } from './image.utils';
import { startTransition, useCallback, useMemo, useState } from 'react';
import { useImageStore } from './image.store';
export const Image = ({ css, className, src, width, height, onLoad, onError, onLoadingComplete, quality = 75, blurSize = 5, blurImageQuality = 10, unoptimized = false, priority = false, noBlur: noBlurProp = false, noAspectRatio = false, noTransition: noTransitionProp = false, loading: loadingProp = 'lazy', ...rest }) => {
    const [state, setState] = useState('loading');
    const [blurState, setBlurState] = useState('loading');
    const getLoader = useImageStore(s => s.getLoader);
    const loaderOptions = useImageStore(s => s.loaderOptions);
    const loading = useMemo(() => (priority ? 'eager' : loadingProp), [loadingProp, priority]);
    const noBlur = useMemo(() => noBlurProp || loading === 'eager', [loading, noBlurProp]);
    const noTransition = useMemo(() => noTransitionProp || loading === 'eager', [loading, noTransitionProp]);
    const widthInt = getInt(width);
    const heightInt = getInt(height);
    const qualityInt = getInt(quality);
    const Wrapper = useMemo(() => (noAspectRatio ? Box : AspectRatio), [noAspectRatio]);
    const srcString = useMemo(() => {
        if (isStaticImport(src)) {
            return src.default;
        }
        return src;
    }, [src]);
    const isUnoptimized = useMemo(() => Boolean(unoptimized ||
        srcString.endsWith('.svg') ||
        !/^https?:\/\//i.test(srcString)), [srcString, unoptimized]);
    const loader = useMemo(() => getLoader(), [getLoader]);
    const imgElementSrc = useMemo(() => isUnoptimized
        ? srcString
        : loader({
            width,
            src: srcString,
            quality: qualityInt,
            options: loaderOptions
        }), [isUnoptimized, loader, loaderOptions, qualityInt, srcString, width]);
    const srcSet = useMemo(() => getSrcSet(srcString, qualityInt, widthInt, loader, loaderOptions, loading === 'eager'), [loader, loaderOptions, loading, qualityInt, srcString, widthInt]);
    const handleLoaded = useCallback((img) => {
        if (loading !== 'eager') {
            setState('loaded');
        }
        if (onLoadingComplete) {
            onLoadingComplete({
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight
            });
        }
    }, [loading, onLoadingComplete]);
    const handleBlurLoaded = useCallback(() => {
        if (loading === 'eager') {
            return;
        }
        setBlurState('loaded');
    }, [loading]);
    const handleLoad = useCallback((event) => {
        startTransition(() => {
            handleLoaded(event.currentTarget);
            if (onLoad) {
                onLoad(event);
            }
        });
    }, [handleLoaded, onLoad]);
    const handleError = useCallback((event) => {
        setState('error');
        if (onError) {
            onError(event);
        }
    }, [onError]);
    const blurDataURL = useMemo(() => isUnoptimized
        ? srcString
        : loader({
            src: srcString,
            quality: blurImageQuality,
            width: width > 360 ? 360 : width,
            blur: blurSize,
            options: loaderOptions
        }), [
        blurImageQuality,
        blurSize,
        isUnoptimized,
        loader,
        loaderOptions,
        srcString,
        width
    ]);
    const ref = useCallback((img) => {
        if (!img) {
            return;
        }
        if (onError) {
            // If the image has an error before react hydrates, then the error is lost.
            // The workaround is to wait until the image is mounted which is after hydration,
            // then we set the src again to trigger the error handler (if there was an error).
            img.src = img.src;
        }
        if (img.complete) {
            handleLoaded(img);
        }
    }, [handleLoaded, onError]);
    const blurRef = useCallback((img) => {
        if (!img) {
            return;
        }
        if (img.complete) {
            handleBlurLoaded();
        }
    }, [handleBlurLoaded]);
    return (_jsxs(Wrapper, { css: { ...css, overflow: 'hidden', position: 'relative' }, className: className, ratio: widthInt / heightInt, children: [!noBlur && !unoptimized && (_jsx(StyledImage, { ref: blurRef, width: widthInt, height: heightInt, alt: rest.alt, 
                // decoding="async"
                isLoaded: blurState === 'loaded' && state !== 'loaded', loading: "lazy", noTransition: noTransition, src: blurDataURL, onLoad: handleBlurLoaded })), _jsx(StyledImage, { ref: ref, css: noBlur
                    ? {}
                    : {
                        position: 'absolute',
                        zIndex: 0,
                        inset: 0
                    }, width: widthInt, height: heightInt, alt: rest.alt, 
                // decoding={loading === 'lazy' ? 'async' : 'auto'}
                isLoaded: state === 'loaded' || state === 'error', loading: loading, noTransition: noTransition, sizes: isUnoptimized ? undefined : IMAGE_SIZES_PROP, src: imgElementSrc, srcSet: isUnoptimized ? undefined : srcSet, onError: handleError, onLoad: handleLoad, ...(priority ? { fetchPriority: 'high' } : {}) })] }));
};
Image.displayName = 'Image';
const StyledImage = styled('img', {
    w: '$full',
    h: 'auto',
    maxW: '$full',
    display: 'block',
    // opacity: 0,
    // willChange: 'opacity',
    // transition: 'opacity 0.3s ease-in-out',
    variants: {
        isLoaded: {
            true: {
            // opacity: 1
            }
        },
        noTransition: {
            true: {
                opacity: 1,
                transition: 'none',
                willChange: 'initial'
            }
        }
    }
});
